import { DkvProvider, OfferProvider, VignetteProvider } from '@adac-mp/shared';
import { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import React from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import isBrowser from 'is-browser';
import fetch from 'cross-fetch';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ToastProvider } from '@adac-mp/components';
import { App } from './app';

type FeatureAppConfig = {
	environment?: string;
};

interface IProviderProps {
	children: React.ReactNode;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServices, FeatureAppConfig> = {
	dependencies: {
		featureServices: {
			's2:serialized-state-manager': '^1.0.0',
		},
	},
	optionalDependencies: {
		featureServices: { 's2:async-ssr-manager': '^1.0.0' },
	},
	create: (env) => {
		if (!env.baseUrl) {
			throw new Error("baseUrl isn't set");
		}

		const serializedStateManager = env.featureServices['s2:serialized-state-manager'] as SerializedStateManagerV1;
		const apolloCache = new InMemoryCache();

		if (isBrowser) {
			const apolloState = JSON.parse(serializedStateManager.getSerializedState() ?? '{}')?.apolloState;

			if (apolloState) {
				apolloCache.restore(apolloState);
			}
		}

		const contentfulSpaceId = process.env.CONTENTFUL_SPACE_ID ?? '';
		const contentfulAccessToken = process.env.CONTENTFUL_ACCESS_TOKEN ?? '';
		const contentfulEnvironment = process.env.CONTENTFUL_ENVIRONMENT ?? '';

		const authLink = setContext((_, { headers }) => {
			return {
				headers: {
					...headers,
					authorization: `Bearer ${contentfulAccessToken}`,
				},
			};
		});

		const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
			ssrMode: !isBrowser,
			cache: apolloCache,
			link: authLink.concat(
				new HttpLink({
					uri: `https://graphql.contentful.com/content/v1/spaces/${contentfulSpaceId}/environments/${contentfulEnvironment}`,
					fetch,
				}),
			),
		});

		// TODO: Find alternative Provider Routing Strategy
		const Providers = ({ children }: IProviderProps) => (
			<OfferProvider>
				<DkvProvider>
					<VignetteProvider>
						<ToastProvider>{children}</ToastProvider>
					</VignetteProvider>
				</DkvProvider>
			</OfferProvider>
		);

		return {
			render: () => (
				<ApolloProvider client={client}>
					<Providers>
						<SkeletonTheme baseColor="#FFFFFF" highlightColor="#EBEBEB">
							<App />
						</SkeletonTheme>
					</Providers>
				</ApolloProvider>
			),
		};
	},
};

export default featureAppDefinition;
